import {Form, FormInstance} from 'antd';
import {useCallback} from 'react';

import {Text} from '../../../../../../../../component/text/text';
import {AdditionalInfo} from '../../../../../../../../layout/additional-info/additional-info';
import {Locale} from '../../../../../../../../provider/locale/locale';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {useFormRules} from '../../../../../../../../service/form/form-rules-hook';
import {saveGeneratedImage} from '../../../../../../../../service/posts/posts-api';
import {
    GoogleButtonTypeEnum,
    PostAiSuggestApplyType,
    PostAiSuggestHookType,
    PostFormType,
    PostImageType,
    PostSourceEnum,
    SourcePostFormFieldsEnum,
} from '../../../../../../../../service/posts/posts-types';
import {useRefreshId} from '../../../../../../../../util/hook';
import {PostFormItemPropsType} from '../../../../post-form-type';

import {PostAiSuggest} from './ai-suggest/post-ai-suggest';
import {PostTextField} from './post-text-field';
import {MIN_POST_TEXT_LENGTH} from './post-text-helper';
import * as styles from './post-text-item.scss';

type PropsType = {
    formInstance: FormInstance<PostFormType>;
    suggestSettings: PostAiSuggestHookType;
    maxLength: number;
    fieldName: Array<string | number>;
    catalog: PostSourceEnum;
    postFieldName: Array<string | number>;
} & PostFormItemPropsType;

export function PostTextItem(props: PropsType): JSX.Element {
    const {formInstance, suggestSettings, maxLength, fieldName, disabled, catalog, postFieldName} = props;

    const {minLengthFieldRule, maxLengthFieldRule, requiredFieldRule} = useFormRules();
    const {refresh: refreshFormItem, refreshId: formItemKey} = useRefreshId();
    const {snackbar} = useSnackbar();

    const applyCallToAction = useCallback(
        (callToActionUrl: string) => {
            const buttonLinkFieldName = [...postFieldName, SourcePostFormFieldsEnum.ButtonLink];

            if (catalog === PostSourceEnum.facebook) {
                formInstance.setFieldValue(buttonLinkFieldName, callToActionUrl);
                return;
            }

            if (catalog === PostSourceEnum.google) {
                const buttonType = formInstance.getFieldValue([
                    ...postFieldName,
                    SourcePostFormFieldsEnum.ButtonType,
                ]) as GoogleButtonTypeEnum | undefined;

                if (buttonType && buttonType !== GoogleButtonTypeEnum.Unspecified) {
                    formInstance.setFieldValue(buttonLinkFieldName, callToActionUrl);
                }
            }
        },
        [catalog, formInstance, postFieldName]
    );

    const handleApplySuggest = useCallback(
        async (apply: PostAiSuggestApplyType) => {
            let permanentImageUrl: string | null = null;

            if (apply.image) {
                try {
                    // save-on-select: copies the temporary preview into permanent post storage
                    const response = await saveGeneratedImage(apply.image.url);

                    permanentImageUrl = response.image;
                } catch {
                    // preview may be expired (410) or service failed: continue with text only
                    snackbar.error({
                        message: <Locale stringKey="POSTS_AI_GENERATION__ERROR__IMAGE_TITLE" />,
                        description: <Locale stringKey="POSTS_SUGGESTS_ERROR__CONTENT" />,
                    });
                }
            }

            formInstance.setFieldValue(fieldName, apply.text);

            if (permanentImageUrl) {
                const imagesFieldName = [...postFieldName, SourcePostFormFieldsEnum.Images];
                const currentImages =
                    (formInstance.getFieldValue(imagesFieldName) as Array<PostImageType> | undefined) ?? [];

                formInstance.setFieldValue(imagesFieldName, [
                    ...currentImages.map((image) => ({...image, isMain: false})),
                    {url: permanentImageUrl, isMain: true},
                ]);
            }

            if (apply.cta?.url) {
                applyCallToAction(apply.cta.url);
            }

            refreshFormItem();
        },
        [applyCallToAction, fieldName, formInstance, postFieldName, refreshFormItem, snackbar]
    );

    return (
        <div>
            <Form.Item
                className={styles.PostTextItem}
                initialValue=""
                key={formItemKey}
                label={
                    <Text block className={styles.PostTextItem_label}>
                        <Locale stringKey="POSTS_FORM__CONTENT__LABEL" />
                        &nbsp;
                        <AdditionalInfo title={<Locale stringKey="POSTS_FORM__CONTENT__LABEL" />}>
                            <Locale stringKey="POSTS_FORM__CONTENT__TOOLTIP" />
                        </AdditionalInfo>
                    </Text>
                }
                name={fieldName}
                required
                rules={
                    !disabled
                        ? [requiredFieldRule, minLengthFieldRule(MIN_POST_TEXT_LENGTH), maxLengthFieldRule(maxLength)]
                        : []
                }
            >
                <PostTextField disabled={disabled} maxLength={maxLength} />
            </Form.Item>

            {!disabled && (
                <PostAiSuggest
                    catalog={catalog}
                    onApplySuggest={handleApplySuggest}
                    suggestSettings={suggestSettings}
                />
            )}
        </div>
    );
}
